import Axios, { AxiosRequestConfig } from 'axios';
import { getRequired } from '@excent-one/config';
import { withErrorHandling, withErrorNotification } from '@excent-one/http';
import { getArbitrIasCsrfToken } from '@excent-one/auth-arbitr-ias';
import qs from 'qs';

const client = Axios.create({
  baseURL: getRequired('VITE_APP_API_BASE_PATH'),
  // 10s was too aggressive for real-world conditions - confirmed live (Bug 157949 Result 1): a
  // 25MB Evidence file upload aborted with a generic "Network Error" at exactly ~10013ms. File
  // uploads override this per-request (see IncidentRecordFileContext.tsx), but ordinary JSON API
  // calls sharing this client benefit from more slack too rather than failing on a slow network.
  timeout: 3 * 60 * 1000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    serialize: (params) => qs.stringify(params, { allowDots: true, arrayFormat: 'indices' }),
  },
});

client.interceptors.request.use((config) => {
  const token = getArbitrIasCsrfToken();
  if (token) {
    config.headers['X-BFF-CSRF'] = token;
  }
  return config;
});

// The generated zod schemas type date/date-time fields as `Date` (override.useDates +
// zod.coerce in phrmpc.config.ts), but JSON has no native date type, so every response is
// still plain ISO strings on the wire. This revives them into real Date instances so the
// generated types match what's actually in memory - without it, `.getMonth()`/`.toISOString()`
// etc. on a "Date"-typed field would throw at runtime.
//
// The trailing offset/"Z" is optional - some endpoints (e.g. plain .NET `DateTime` fields
// without `DateTimeOffset`, like schedule fromSchedule/toSchedule) serialize as bare
// "yyyy-MM-ddTHH:mm:ss" with no zone suffix, even though the zod schema's `.datetime({offset:
// true})` implies one - that constraint is never actually enforced since nothing calls
// zod's `.parse()` on responses.
const ISO_DATE_TIME_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;

const reviveDates = (value: unknown): unknown => {
  if (typeof value === 'string' && ISO_DATE_TIME_PATTERN.test(value)) {
    return new Date(value);
  }
  if (Array.isArray(value)) {
    return value.map(reviveDates);
  }
  if (value !== null && typeof value === 'object') {
    for (const key of Object.keys(value as Record<string, unknown>)) {
      (value as Record<string, unknown>)[key] = reviveDates(
        (value as Record<string, unknown>)[key],
      );
    }
    return value;
  }
  return value;
};

client.interceptors.response.use((response) => {
  response.data = reviveDates(response.data);
  return response;
});

// The mirror-image problem on the way out: a bare "yyyy-MM-ddTHH:mm:ss" (no zone) parses as
// *local* wall-clock time (per the ECMA-262 date-time string spec), so `reviveDates` above
// correctly hands back e.g. Aug 21 00:00 local for a picker's selected calendar day. But
// `JSON.stringify`'s default Date serialization always calls `.toISOString()`, which converts
// to UTC - for any positive UTC offset (e.g. UTC+8), local midnight becomes 16:00 the
// *previous* day, silently shifting the calendar date back by one on the wire. Serializing
// outgoing Dates using their local components instead (matching the no-offset format the
// backend itself sends) keeps the round trip symmetric.
const pad = (value: number) => value.toString().padStart(2, '0');

const toLocalDateTimeString = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T` +
  `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

const localizeDates = (value: unknown): unknown => {
  if (value instanceof Date) {
    return toLocalDateTimeString(value);
  }
  if (Array.isArray(value)) {
    return value.map(localizeDates);
  }
  if (value !== null && typeof value === 'object') {
    for (const key of Object.keys(value as Record<string, unknown>)) {
      (value as Record<string, unknown>)[key] = localizeDates(
        (value as Record<string, unknown>)[key],
      );
    }
    return value;
  }
  return value;
};

client.interceptors.request.use((config) => {
  if (config.data !== undefined) {
    config.data = localizeDates(config.data);
  }
  return config;
});

withErrorNotification(client);
withErrorHandling(client);

export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  const promise = client({
    ...config,
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...config.headers,
      ...options?.headers,
    },
  }).then(({ data }) => data);

  return promise;
};

export default client;
